<script setup lang="ts">
import profilePhotoPlaceholder from '@/assets/img/profile-photo-placeholder.svg';
import { useAuthStore } from '@/modules/auth/store/auth';
import { useCartStore } from '@/store/cart';
import { useMobileStore } from '@/store/mobile';
import { useNotificationStore } from '@/stores/notifications';
import type { SidebarMenu } from '@/types/SidebarMenu';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import AppAvatar from './base/AppAvatar.vue';
import AppButton from './base/AppButton.vue';
import AppLogo from './base/AppLogo.vue';
import AppTypography from './base/AppTypography.vue';
import MenuItemList from './sidebar/MenuItemList.vue';
import Notifications from './sidebar/Notifications.vue';
import ThemeToggle from './ThemeToggle.vue';

interface Props {
  prependMenu?: SidebarMenu[];
  appendMenu?: SidebarMenu[];
}

interface MenuItem {
  title: string;
  subMenuItems: SidebarMenu[];
}

const { prependMenu = [], appendMenu = [] } = defineProps<Props>();

const authStore = useAuthStore();
const { user, isCustomer, isDriver, hasCompletedOnboarding } = storeToRefs(authStore);
const mobileStore = useMobileStore();
const { showMenu } = storeToRefs(mobileStore);
const { toggleMenu } = mobileStore;

const { signOut } = useAuth();
const { showConfirmDialog } = useConfirmDialog();
const cartStore = useCartStore();
const notificationStore = useNotificationStore();

const handleLogout = async () => {
  showConfirmDialog({
    title: 'Log out?',
    description: 'Are you sure you want to log out?',
    confirmText: 'Log out',
    confirmTextColor: 'error',
    cancelText: 'Cancel',
    hasCancel: true,
    confirm: async () => {
      await signOut({ callbackUrl: '/' });
      user.value = null;
      cartStore.resetCarts();
    },
  });
};

// Navigation state
const currentView = ref<string>('main');
const isSliding = ref<boolean>(false);
const slideDirection = ref<'forward' | 'backward'>('forward');

onMounted(async () => {
  if (user.value) {
    await notificationStore.fetchUnreadCount();
  }
});

const navigateToSubMenu = async (key: string) => {
  isSliding.value = true;
  slideDirection.value = 'forward';
  currentView.value = key;
  isSliding.value = false;
};

const customerMenus = computed<MenuItem[]>(() => {
  return [
    {
      title: 'Address',
      subMenuItems: [
        {
          title: user.value?.address || '',
          to: '/account/edit-address',
          icon: 'mdi-map-marker-outline',
          class: 'sidebar-address',
        },
      ],
    },
    {
      title: 'Buying',
      subMenuItems: [
        {
          title: 'Orders',
          to: '/customer/orders',
        },
      ],
    },
  ];
});

const merchantMenus = computed<MenuItem[]>(() => {
  return [
    {
      title: 'Selling',
      subMenuItems: [
        {
          title: 'Orders',
          to: '/merchant/orders',
        },
        {
          title: 'Listings',
          to: '/merchant/listing',
        },
        {
          title: 'Edit merchant profile',
          to: '/merchant/edit-profile',
        },
        {
          title: 'Connect to Tradel',
          icon: 'mdi-key-chain',
          to: '/merchant/integrations',
        },
        {
          title: 'Manage Payout',
          action: async () => {
            if (!hasCompletedOnboarding.value) {
              await navigateTo('/merchant/business-details');
            } else {
              await navigateTo('/merchant/stripe-account');
            }

            showMenu.value = false;
          },
        },
        {
          title: 'Merchant Dashboard',
          iconAppend: 'mdi-open-in-new',
          action: async () => {
            window.open('/merchant/dashboard', '_blank');
            showMenu.value = false;
          },
        },
      ],
    },
  ];
});

const driverMenus = computed<MenuItem[]>(() => {
  return [
    {
      title: 'Delivering',
      subMenuItems: [
        {
          title: 'Orders',
          to: '/driver/orders',
        },
      ],
    },
  ];
});

const menuItems = computed<MenuItem[]>(() => {
  return [
    {
      title: '',
      subMenuItems: [
        {
          title: 'Notifications',
          icon: 'mdi-bell-outline',
          badge: notificationStore.unreadCount,
          action: () => navigateToSubMenu('Notifications'),
        },
      ],
    },
    ...(isDriver.value ? driverMenus.value : isCustomer.value ? customerMenus.value : merchantMenus.value),
    {
      title: 'Profile',
      subMenuItems: [
        ...(isCustomer.value
          ? [
              {
                title: 'Edit Profile',
                to: '/account/edit-profile',
              },
            ]
          : []),
        {
          title: 'Account Settings',
          to: '/account/account-settings',
        },
        {
          title: 'Help Center',
          to: '/account/help-center',
        },
        {
          title: 'About',
          to: '/account/about',
        },
        {
          title: 'Logout',
          iconAppend: 'mdi-logout',
          action: handleLogout,
        },
      ],
    },
  ];
});

const guestItems = ref<MenuItem[]>([
  {
    title: '',
    subMenuItems: [
      {
        title: 'Customer',
        to: '/register',
      },
      {
        title: 'Merchant',
        to: '/merchant/sign-up',
      },
    ],
  },
  {
    title: 'Already have an account?',
    subMenuItems: [
      {
        title: 'Sign in',
        to: '/login',
      },
    ],
  },
]);

const navigations = computed(() => {
  if (user.value) {
    return menuItems.value;
  } else {
    return guestItems.value;
  }
});

// Sub-menu content
const subMenus = {
  'edit-profile': [
    { title: 'Personal Information', action: () => console.log('Personal Info') },
    { title: 'Profile Picture', action: () => console.log('Profile Picture') },
    { title: 'Contact Details', action: () => console.log('Contact Details') },
  ],
  'account-settings': [
    { title: 'Email address', action: () => console.log('Privacy') },
    { title: 'Notification Preferences', action: () => console.log('Notifications') },
    { title: 'Security', action: () => console.log('Security') },
    { title: 'Delete Account', action: () => console.log('Delete Account') },
  ],
  'help-center': [
    { title: 'FAQ', action: () => console.log('FAQ') },
    { title: 'Contact Support', action: () => console.log('Contact Support') },
    { title: 'Report a Problem', action: () => console.log('Report Problem') },
  ],
  about: [
    { title: 'Terms of Service', action: () => console.log('Terms') },
    { title: 'Privacy Policy', action: () => console.log('Privacy Policy') },
    { title: 'App Version', action: () => console.log('Version') },
  ],
};

const navigateBack = () => {
  isSliding.value = true;
  slideDirection.value = 'backward';
  currentView.value = 'main';
  isSliding.value = false;
};

const getCurrentSubMenu = () => {
  return subMenus[currentView.value as keyof typeof subMenus] || [];
};

const getCurrentTitle = () => {
  const allSubItems = navigations.value.flatMap((item) => item.subMenuItems);
  const item = allSubItems.find((item) => item.title === currentView.value);

  return item?.title || '';
};

watch(
  showMenu,
  async (isVisible) => {
    if (isVisible && user.value) {
      await notificationStore.fetchUnreadCount();
    }
  },
  {
    immediate: true,
  }
);
</script>

<template>
  <v-navigation-drawer v-model="showMenu" width="373" temporary data-testid="sidebar">
    <div class="sidebar-content" :class="{ sliding: isSliding }">
      <!-- Main View -->
      <Transition :name="slideDirection === 'forward' ? 'slide-forward' : 'slide-backward'" mode="out-in">
        <div v-if="currentView === 'main'" key="main" class="view-container">
          <div class="pl-2">
            <div class="d-flex justify-space-between align-center drawer-header">
              <ThemeToggle />
              <AppButton
                variant="text"
                size="small"
                rounded="full"
                color="-primary"
                class="pa-0 circle close-button-sidebar"
                @click="toggleMenu()"
              >
                <v-icon icon="mdi-close" size="20" />
              </AppButton>
            </div>
          </div>
          <div class="d-flex flex-column pl-4 px-md-4">
            <div class="d-flex align-center ga-4 mb-10" v-if="user">
              <AppAvatar :src="profilePhotoPlaceholder" :size="64" />
              <AppTypography element="h1" type="title-1" class="text-truncate full-name">
                {{ isCustomer ? user?.full_name : user?.account?.business_name }}
              </AppTypography>
            </div>

            <div v-else class="mb-10">
              <AppLogo class="mb-6" height="72" width="72" />
              <AppTypography element="h1" type="title01">Create an account</AppTypography>
            </div>

            <template v-for="(item, i) in navigations" :key="`${item.title}-${i}`">
              <AppTypography v-if="item.title" element="h5" type="headline-1" class="mb-3">
                {{ item.title }}
              </AppTypography>

              <MenuItemList :items="item.subMenuItems" />
            </template>
          </div>
        </div>

        <!-- Sub Views -->
        <div v-else key="sub" class="view-container">
          <div class="pl-2">
            <div class="d-flex flex-row justify-space-between align-center drawer-header">
              <AppButton
                variant="text"
                color="-primary"
                size="small"
                class="pa-0 back"
                rounded="xl"
                @click="navigateBack"
              >
                <v-icon :icon="currentView === 'Notifications' ? 'mdi-chevron-left' : 'mdi-close'" size="20" />
              </AppButton>
              <AppTypography element="h5" type="headline-1">{{ getCurrentTitle() }}</AppTypography>
              <div class="filler"></div>
            </div>
          </div>
          <div class="d-flex flex-column px-4">
            <Notifications v-if="currentView === 'Notifications'" />
          </div>
        </div>
      </Transition>
    </div>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
.back {
  min-width: 32px !important;
  min-height: 32px !important;
}

.v-navigation-drawer {
  z-index: 1400 !important;
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
  border: 1px solid rgb(var(--v-theme-stroke));
  overflow: hidden;

  .drawer-header {
    height: 58px;
    padding-inline-end: 16px;
  }

  .v-list-item:not(:last-child) {
    border-bottom: 1px solid rgb(var(--v-theme-stroke));
  }
}

.sidebar-content {
  position: relative;
  overflow: hidden;

  &.sliding {
    pointer-events: none;
  }
}

.view-container {
  width: 100%;
}

.full-name {
  line-height: 1.5;
}

// Forward slide transition (left to right)
.slide-forward-enter-active,
.slide-forward-leave-active {
  transition: transform 0.15s ease-out;
}

.slide-forward-enter-from {
  transform: translateX(100%);
}

.slide-forward-leave-to {
  transform: translateX(-100%);
}

.slide-forward-enter-to,
.slide-forward-leave-from {
  transform: translateX(0);
}

// Backward slide transition (right to left)
.slide-backward-enter-active,
.slide-backward-leave-active {
  transition: transform 0.15s ease-out;
}

.slide-backward-enter-from {
  transform: translateX(-100%);
}

.slide-backward-leave-to {
  transform: translateX(100%);
}

.slide-backward-enter-to,
.slide-backward-leave-from {
  transform: translateX(0);
}

@media (max-width: 768px) {
  .v-navigation-drawer {
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
  }
}
</style>
